const buildFormatter = (options = {}) =>
  new Intl.NumberFormat('es-AR', options);

const groupIntegerDigits = (digits) => {
  const cleanDigits = String(digits || '').replace(/\D/g, '');
  if (!cleanDigits) return '';
  return cleanDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// FIX IMPORTANTE #22: helper de moneda configurable por organización.
//
// Defaults locale 'es-AR' / currency 'ARS'. Si la org configura otra moneda
// (vía settings o por organization.default_currency cuando exista), se pueden
// pasar como opciones. Mientras no haya un currency global por org, las páginas
// pueden:
//   1) Persistir 'impulza:<orgId>:currency' / 'impulza:<orgId>:locale' en localStorage.
//   2) Llamar a `getOrgFormatPreferences(orgId)` para leer esos settings.
//   3) Pasar a `formatCurrency(value, { locale, currency })`.
//
// De este modo dejamos de hardcodear es-AR/ARS en cada `fmtMoney` regado por el código.
const FALLBACK_LOCALE = 'es-AR';
const FALLBACK_CURRENCY = 'ARS';

export const getOrgFormatPreferences = (orgId) => {
  try {
    if (!orgId) return { locale: FALLBACK_LOCALE, currency: FALLBACK_CURRENCY };
    const locale = localStorage.getItem(`impulza:${orgId}:locale`) || FALLBACK_LOCALE;
    const currency = localStorage.getItem(`impulza:${orgId}:currency`) || FALLBACK_CURRENCY;
    return { locale, currency };
  } catch {
    return { locale: FALLBACK_LOCALE, currency: FALLBACK_CURRENCY };
  }
};

export const setOrgFormatPreferences = (orgId, { locale, currency } = {}) => {
  try {
    if (!orgId) return;
    if (locale) localStorage.setItem(`impulza:${orgId}:locale`, locale);
    if (currency) localStorage.setItem(`impulza:${orgId}:currency`, currency);
  } catch {
    // ignorar
  }
};

/**
 * Formato de moneda respetando locale + currency de la organización.
 *
 * @param {number} value - el monto numérico
 * @param {object} [opts]
 * @param {string} [opts.locale] - default 'es-AR'
 * @param {string} [opts.currency] - default 'ARS'
 * @param {number} [opts.minimumFractionDigits] - default 2
 * @param {number} [opts.maximumFractionDigits] - default 2
 * @param {string} [opts.orgId] - si lo pasás, se leen los settings de localStorage
 */
export const formatCurrency = (value, opts = {}) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return '';

  const fromOrg = opts.orgId ? getOrgFormatPreferences(opts.orgId) : {};
  const locale = opts.locale || fromOrg.locale || FALLBACK_LOCALE;
  const currency = opts.currency || fromOrg.currency || FALLBACK_CURRENCY;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: opts.minimumFractionDigits ?? 2,
      maximumFractionDigits: opts.maximumFractionDigits ?? 2,
    }).format(numericValue);
  } catch {
    // Fallback si el locale/currency es inválido
    return `$ ${numericValue.toFixed(2)}`;
  }
};

export const formatNumberAr = (value, options = {}) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';
  return buildFormatter(options).format(numericValue);
};

export const formatCurrencyAr = (value, options = {}) => {
  const formatted = formatNumberAr(value, {
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  });

  return `${options.prefix ?? '$ '}${formatted || '0,00'}`;
};

export const normalizeLocalizedNumberInput = (
  input,
  { decimals = 2, allowNegative = false, preferSingleDotAsDecimal = false } = {},
) => {
  const rawInput = String(input ?? '');
  const sanitizedInput = rawInput.replace(/[^\d,.-]/g, '');
  const negative = allowNegative && sanitizedInput.trim().startsWith('-');
  const unsignedInput = sanitizedInput.replace(/-/g, '');

  if (!unsignedInput) {
    return {
      display: negative ? '-' : '',
      raw: '',
      number: null,
      hasValue: false,
    };
  }

  const commaIndex = unsignedInput.lastIndexOf(',');
  const dotIndex = unsignedInput.lastIndexOf('.');
  const dotCount = (unsignedInput.match(/\./g) || []).length;

  let decimalIndex = -1;
  let trailingDecimal = false;

  if (commaIndex >= 0 && dotIndex >= 0) {
    // Conviven coma y punto (ej. AR "1.234,56" o US "1,234.56"): el separador
    // decimal es el que aparece ÚLTIMO; el otro es de miles. Antes la coma ganaba
    // siempre y "1,234.56" se corrompía a 1.23 (error x1000). Bug P5.
    decimalIndex = Math.max(commaIndex, dotIndex);
    trailingDecimal = decimalIndex === unsignedInput.length - 1;
  } else if (commaIndex >= 0) {
    decimalIndex = commaIndex;
    trailingDecimal = commaIndex === unsignedInput.length - 1;
  } else if (dotIndex >= 0) {
    const digitsAfterDot = unsignedInput.slice(dotIndex + 1).replace(/\D/g, '');
    const digitsBeforeDot = unsignedInput.slice(0, dotIndex).replace(/\D/g, '');
    const isTrailingDot = dotIndex === unsignedInput.length - 1;
    const canTreatDotAsDecimal =
      dotCount === 1 &&
      decimals > 0 &&
      (
        (preferSingleDotAsDecimal && (digitsAfterDot.length > 0 || isTrailingDot)) ||
        (
          digitsAfterDot.length > 0 &&
          digitsAfterDot.length <= decimals &&
          !(digitsAfterDot.length === 3 && digitsBeforeDot.length >= 1)
        )
      );

    if (canTreatDotAsDecimal) {
      decimalIndex = dotIndex;
      trailingDecimal = isTrailingDot;
    }
  }

  const integerSource = decimalIndex >= 0 ? unsignedInput.slice(0, decimalIndex) : unsignedInput;
  const fractionSource = decimalIndex >= 0 ? unsignedInput.slice(decimalIndex + 1) : '';

  const integerDigits = integerSource.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  const fractionDigits = fractionSource.replace(/\D/g, '').slice(0, Math.max(0, decimals));
  const hasDigits = integerDigits.length > 0 || fractionDigits.length > 0;

  if (!hasDigits) {
    return {
      display: negative ? '-' : '',
      raw: '',
      number: null,
      hasValue: false,
    };
  }

  const displayInteger = integerDigits ? groupIntegerDigits(integerDigits) : '0';
  let display = `${negative ? '-' : ''}${displayInteger}`;
  if (decimals > 0 && (fractionDigits.length > 0 || trailingDecimal)) {
    display += `,${fractionDigits}`;
  }

  const raw = `${negative ? '-' : ''}${integerDigits || '0'}${fractionDigits ? `.${fractionDigits}` : ''}`;
  const number = Number(raw);

  return {
    display,
    raw,
    number: Number.isFinite(number) ? number : null,
    hasValue: true,
  };
};

export const parseLocalizedNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  return normalizeLocalizedNumberInput(value, options).number;
};

export const formatLocalizedNumberInputValue = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') {
    return formatNumberAr(value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: options.decimals ?? 2,
    });
  }
  return normalizeLocalizedNumberInput(value, options).display;
};
